import { formatDateTimeWithZone } from "@/lib/utils"

// OverrideableBool represents a boolean value that can be overridden.
export interface OverrideableBool {
  value?: boolean
  useDefault: boolean
}

// OverrideableInt represents an integer value that can be overridden.
export interface OverrideableInt {
  value?: number
  useDefault: boolean
}

export interface GapDeviceConfig {
  id: number
  deviceId: string
  clientId: string
  areaCode: string
  vendorName: string
  vendorDeviceId: string
  featureFlagUserInstructions: OverrideableBool
  necMatchScoreThreshold: OverrideableInt
  createdAt: string
  lastUpdated: string
}

export interface CreateGapDeviceRequest {
  deviceId: string
  clientId: string
  areaCode: string
  vendorName: string
  vendorDeviceId: string
  necMatchScoreThreshold?: OverrideableInt
  featureFlagUserInstructions?: OverrideableBool
}

export interface UpdateGapDeviceRequest {
  clientId?: string // Required by backend; send current value when updating (read-only in UI)
  vendorDeviceId?: string
  vendorName?: string
  areaCode?: string
  necMatchScoreThreshold?: OverrideableInt
  featureFlagUserInstructions?: OverrideableBool
}

// UI-friendly device representation used in components
export interface GapDeviceUI {
  id: string
  deviceId: string
  vendorDeviceId: string
  vendorName: string
  clientId: string
  areaCode?: string
  clientName: string
  venueName: string
  status: "Active" | "Inactive"
  necThreshold: string
  necThresholdUseDefault: boolean
  userInstructionsEnabled: boolean
  userInstructionsUseDefault: boolean
  lastUpdated: string
  lastUpdatedRaw: string // Raw ISO string for sorting
}

// Transform API response to UI format
export function toGapDeviceUI(device: GapDeviceConfig, clientName: string, venueName: string): GapDeviceUI {
  return {
    id: device.deviceId,
    deviceId: device.deviceId,
    vendorDeviceId: device.vendorDeviceId,
    vendorName: device.vendorName,
    clientId: device.clientId,
    areaCode: device.areaCode,
    clientName,
    venueName,
    status: "Active", // API doesn't provide status, defaulting to Active
    necThreshold: device.necMatchScoreThreshold.value?.toString() || "0",
    necThresholdUseDefault: device.necMatchScoreThreshold.useDefault,
    userInstructionsEnabled: device.featureFlagUserInstructions.value ?? false,
    userInstructionsUseDefault: device.featureFlagUserInstructions.useDefault,
    lastUpdated: formatDateTimeWithZone(device.lastUpdated),
    lastUpdatedRaw: device.lastUpdated,
  }
}
